import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDocumentTitle } from '@uidotdev/usehooks';
import {
  concat,
  difference,
  includes,
  intersection,
  map,
  sortBy,
  union,
  without,
  xor,
} from 'lodash';
import { useState } from 'react';
import {
  Loading,
  Title,
  useNotify,
} from 'react-admin';
import { When } from 'react-if';
import { useNavigate, useParams } from 'react-router-dom';

import type { AuthorizationPermissionGroup, DisplayContact } from '@/Api/studioServerTypes';
import { authHttpClient } from '@/Auth/authProvider';

interface PermissionEntry {
  Permission: string;
  Implicit: boolean;
}

export default function MemberPermissions(): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { contactIdentifier } = useParams<{ contactIdentifier: string }>();
  const [permissionDraft, setPermissionDraft] = useState<string[] | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const toggleGroup = (groupId: string) => setCollapsedGroups(prev => xor(prev, [groupId]));
  const {
    data: contact,
  } = useQuery<DisplayContact>({
    queryKey: ['contact-display', contactIdentifier],
    enabled: !!contactIdentifier,
    queryFn: async () => {
      const response = await authHttpClient(`/api/contacts/${contactIdentifier}/display`);

      return response.json as DisplayContact;
    },
  });
  const {
    data: permissionGroups = [],
    isError: hasPermissionGroupsError,
    isPending: isLoadingPermissionGroups,
  } = useQuery<AuthorizationPermissionGroup[]>({
    queryKey: ['authorization-permissions'],
    enabled: !!contactIdentifier,
    queryFn: async () => {
      const response = await authHttpClient('/api/authorization/permissions');

      return response.json as AuthorizationPermissionGroup[];
    },
  });
  const {
    data: savedPermissions = [],
    isError: hasSavedPermissionsError,
    isPending: isLoadingSavedPermissions,
  } = useQuery<PermissionEntry[]>({
    queryKey: ['authorization-member-permissions', contactIdentifier],
    enabled: !!contactIdentifier,
    queryFn: async () => {
      const response = await authHttpClient(`/api/authorization/permissions/${contactIdentifier}`);

      return response.json as PermissionEntry[];
    },
  });
  const explicitPermissionIds = savedPermissions
    .filter(p => !p.Implicit)
    .map(p => p.Permission);
  const implicitPermissionIds = savedPermissions
    .filter(p => p.Implicit)
    .map(p => p.Permission);
  const savePermissionsMutation = useMutation<PermissionEntry[], Error, string[]>({
    mutationFn: async (permissionIds) => {
      const response = await authHttpClient(
        `/api/authorization/permissions/${contactIdentifier}`,
        {
          method: 'PUT',
          body: JSON.stringify(permissionIds),
        },
      );

      return response.json as PermissionEntry[];
    },
    onSuccess: (permissions) => {
      queryClient.setQueryData(['authorization-member-permissions', contactIdentifier], permissions);
      setPermissionDraft(null);
      notify('Permissions saved', { type: 'success' });
    },
    onError: () => {
      notify('Unable to save permissions', { type: 'error' });
    },
  });

  useDocumentTitle('Permissions');

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
      <Title title='Permissions' />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={2}
      >
        <Box minWidth={0}>
          <Typography
            variant='h5'
            fontWeight={700}
            noWrap
          >
            Permissions
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            noWrap
          >
            {contact?.FullName ?? contactIdentifier}{contact?.Email ? ` — ${contact.Email}` : ''}
          </Typography>
        </Box>

        <Stack direction='row' spacing={1}>
          <Button
            variant='contained'
            disabled={
              !contactIdentifier
              || hasPermissionGroupsError
              || hasSavedPermissionsError
              || permissionDraft === null
              || savePermissionsMutation.isPending
            }
            onClick={() => {
              const toSave = difference(permissionDraft ?? explicitPermissionIds, implicitPermissionIds);
              savePermissionsMutation.mutate(toSave);
            }}
          >
            Save
          </Button>

          <Button
            variant='outlined'
            onClick={() => {
              void navigate('/permissions');
            }}
          >
            Back
          </Button>
        </Stack>
      </Stack>

      <When condition={!contactIdentifier || hasPermissionGroupsError || hasSavedPermissionsError}>
        <Alert severity='error' sx={{ mb: 2 }}>
          {contactIdentifier ? 'Unable to load permissions' : 'Account member is required'}
        </Alert>
      </When>

      <When condition={!!contactIdentifier && (isLoadingPermissionGroups || isLoadingSavedPermissions)}>
        <Loading loadingPrimary='Loading permissions' loadingSecondary='' />
      </When>

      <When
        condition={
          !!contactIdentifier
          && !hasPermissionGroupsError
          && !hasSavedPermissionsError
          && !isLoadingPermissionGroups
          && !isLoadingSavedPermissions
        }
      >
        <Stack spacing={2}>
          {permissionGroups.map((group) => {
            const groupPermissionIds = map(group.Permissions ?? [], p => p.Id!);
            const selectableIds = difference(groupPermissionIds, implicitPermissionIds);
            const activeIds = permissionDraft ?? explicitPermissionIds;
            const implicitCount = intersection(groupPermissionIds, implicitPermissionIds).length;
            const checkedCount = intersection(selectableIds, activeIds).length + implicitCount;
            const allChecked = selectableIds.length > 0 && checkedCount === groupPermissionIds.length;
            const someChecked = checkedCount > 0 && !allChecked;

            const isCollapsed = includes(collapsedGroups, group.Id!);

            return (
              <Box key={group.Id}>
                <Stack direction='row' alignItems='center' mt={2} mb={0.75}>
                  <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    disabled={savePermissionsMutation.isPending || selectableIds.length === 0}
                    onChange={(_, checked) => {
                      setPermissionDraft((currentDraft) => {
                        const base = currentDraft ?? explicitPermissionIds;
                        return checked
                          ? union(base, selectableIds)
                          : difference(base, selectableIds);
                      });
                    }}
                    size='small'
                    sx={{ p: 0.5 }}
                  />
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    textTransform='uppercase'
                    sx={{ flexGrow: 1, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggleGroup(group.Id!)}
                  >
                    {group.Name}
                    <Typography component='span' variant='caption' color='text.disabled' ml={1}>
                      {checkedCount}/{groupPermissionIds.length}
                    </Typography>
                  </Typography>
                  <IconButton size='small' onClick={() => toggleGroup(group.Id!)} sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    {isCollapsed ? <ExpandMoreIcon fontSize='small' /> : <ExpandLessIcon fontSize='small' />}
                  </IconButton>
                </Stack>

                <Collapse in={!isCollapsed}>
                  <Stack spacing={0.75}>
                    {sortBy(group.Permissions ?? [], p => !includes(implicitPermissionIds, p.Id!)).map((permission) => {
                      const isImplicit = implicitPermissionIds.includes(permission.Id!);

                      return (
                        <FormControlLabel
                          key={permission.Id}
                          control={(
                            <Checkbox
                              // Show the checkbox as checked if this permission id is in the current list.
                              checked={
                                isImplicit
                                || (permissionDraft ?? explicitPermissionIds).includes(permission.Id!)
                              }
                              disabled={savePermissionsMutation.isPending || isImplicit}
                              // MUI gives us the new checkbox value as `checked`.
                              // checked === true means the user turned this permission on.
                              // checked === false means the user turned this permission off.
                              onChange={(_, checked) => {
                                setPermissionDraft((currentDraft) => (
                                  checked
                                    // Add this permission id to the draft list.
                                    ? concat(currentDraft ?? explicitPermissionIds, permission.Id!)

                                    // Remove this permission id from the draft list.
                                    : without(currentDraft ?? explicitPermissionIds, permission.Id!)
                                ));
                              }}
                            />
                          )}
                          label={(
                            <Box lineHeight={0.1}>
                              <Typography variant='body1' fontSize={12} fontWeight={400}>
                                {permission.Name}
                              </Typography>
                              <Typography variant='caption' fontSize={9} color='text.secondary'>
                                {permission.Description}
                              </Typography>
                            </Box>
                          )}
                          sx={{
                            'alignItems': 'center',
                            'mx': 0,
                            '& .MuiFormControlLabel-label': {
                              minWidth: 0,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </When>
    </Box>
  );
}
